import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Container,
  Card,
  Grid,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import InsightsIcon from "@mui/icons-material/Insights";
import RuleIcon from "@mui/icons-material/Rule";
import VerifiedIcon from "@mui/icons-material/Verified";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Breadcrumbs from "examples/Breadcrumbs";
import WhatsAppFloat from "components/WhatsAppFloat";
import qualidadeImg from 'assets/images/qualidade/qualidade.jpg'
import bgBanner from "assets/images/bg-presentation.jpg";

/* ----------------- Paleta Ramlis ----------------- */
const BRAND = {
  main: "#036D3D",
  dark: "#004C2A",
  glass: "rgba(1,43,24,.58)",
  line: "rgba(3,109,61,.30)",
  ring: "rgba(3,109,61,.55)",
};

/* ----------------- Helpers ----------------- */
function SectionContainer({ children, sx }) {
  return (
    <Container sx={{ maxWidth: 1100, width: "100%", mx: "auto", ...sx }}>
      {children}
    </Container>
  );
}
SectionContainer.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
};

const FadeIn = ({ children, show, timeout = 400, sx }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [show]);
  return (
    <MKBox
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${timeout}ms ease, transform ${timeout}ms ease`,
        ...sx,
      }}
    >
      {children}
    </MKBox>
  );
};
FadeIn.propTypes = {
  children: PropTypes.node,
  show: PropTypes.bool,
  timeout: PropTypes.number,
  sx: PropTypes.object,
};

/* ----------------- Página ----------------- */
export default function Qualidade() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const parallax = isMobile ? "scroll" : "fixed";

  const [loaded, setLoaded] = useState(false);
   const NAVBAR_PT = { xs: 18, md: 25 };

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <>
      {/* HERO */}
      <MKBox
        role="img"
        aria-label="Ramlis Machine - Qualidade"
        sx={{
          position: "relative",
          pt: NAVBAR_PT,
           minHeight: { xs: 450, md: 400 },
          display: "grid",
          placeItems: "center",
          textAlign: "center",
          color: "#fff",
          backgroundImage: `url(${bgBanner})`,
          backgroundSize: "cover",
          backgroundPosition: { xs: "center", md: "center 35%" },
          backgroundAttachment: { xs: "scroll", md: parallax },
          "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0, 14, 8, 0.75) 0%, rgba(0,76,42,.55) 45%, rgba(0,76,42,.75) 100%)",
          },
        }}
      >
        <FadeIn show={loaded} timeout={600} sx={{ position: "relative", zIndex: 1, width: "100%" }}>
          <SectionContainer>
            <MKBox
              sx={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 0.5,
                px: { xs: 1.5, md: 3 },
                py: { xs: 1, md: 1.25 },
                mx: "auto",
                borderRadius: 2,
                backgroundColor: BRAND.glass,
                backdropFilter: "saturate(1.1) blur(3px)",
                boxShadow: "0 10px 28px rgba(0,76,42,.35)",
              }}
            >
              <MKTypography
                component="h1"
                variant="h3"
                fontWeight="bold"
                color="white"
                mb={0.5}
                sx={{ textShadow: "0 2px 6px rgba(0,76,42,.85)" }}
              >
                Controle de Qualidade
              </MKTypography>
              <MKTypography
                variant="body1"
                color="white"
                sx={{ opacity: 0.95, textShadow: "0 1px 3px rgba(0,76,42,.8)" }}
              >
                Excelência como obrigação e melhoria contínua como prática diária.
              </MKTypography>
            </MKBox>
          </SectionContainer>
        </FadeIn>
      </MKBox>

      {/* Breadcrumbs */}
      <SectionContainer sx={{ mt: -3, mb: 4 }}>
        <Breadcrumbs
          routes={[{ label: "Home", route: "/" }, { label: "Qualidade" }]}
          sx={{ "& .MuiBreadcrumbs-separator": { color: BRAND.main } }}
        />
      </SectionContainer>

      {/* Conteúdo principal */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          backgroundColor: '#fff'
        }}
      >
        <FadeIn show={loaded} timeout={700}>
          <SectionContainer sx={{ py: { xs: 3, md: 5 } }}>
            {/* Bloco de texto introdutório */}
            <Grid container spacing={{ xs: 3, md: 4 }}>
              <Grid item xs={12}>
                <MKTypography variant="h5" fontWeight="bold" sx={{ color: BRAND.dark, mb: 1 }}>
                  Controle de Qualidade
                </MKTypography>
                <MKTypography variant="body2" color="text" paragraph sx={{ lineHeight: 1.85, textAlign: "justify" }}>
                  A qualidade na Ramlis Machine não é apenas um diferencial competitivo, é uma obrigação.
                  Partindo deste princípio, oferecemos serviços que atendem às necessidades e expectativas
                  de nossos clientes.
                </MKTypography>
                <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.85, textAlign: "justify" }}>
                  Nosso objetivo é a satisfação dos clientes, atendendo às suas necessidades e expectativas,
                  oferecendo produtos e serviços de excelente qualidade e conquistando seu respeito.
                  Valorizamos nossos recursos humanos e mantemos compromisso com a melhoria contínua
                  do Sistema de Gestão da Qualidade.
                </MKTypography>
              </Grid>
            </Grid>

            {/* Linha separadora (no lugar da faixa) */}
            <Divider
              sx={{
                my: { xs: 4, md: 6 },
                height: 2,
                border: "none",
                background:
                  "linear-gradient(90deg, rgba(3,109,61,0) 0%, rgba(3,109,61,.35) 25%, rgba(3,109,61,.5) 50%, rgba(3,109,61,.35) 75%, rgba(3,109,61,0) 100%)",
              }}
            />
            


            {/* NOVA LINHA: Pilares da Qualidade (4 cards) */}
            <MKTypography
              variant="button"
              sx={{
                display: "block",
                mb: 2,
                color: BRAND.main,
                fontWeight: 800,
                letterSpacing: ".8px",
                textTransform: "uppercase",
              }}
            >
              Pilares da Qualidade
            </MKTypography>

            <Grid container spacing={2.25} sx={{ mb: { xs: 4, md: 6 } }}>
              {[
                {
                  icon: <PrecisionManufacturingIcon />,
                  title: "Processos padronizados",
                  desc: "Rotinas claras, instruções de trabalho e rastreabilidade fim-a-fim.",
                },
                {
                  icon: <RuleIcon />,
                  title: "Metrologia e controle",
                  desc: "Inspeções dimensionais, registros e evidências por operação/lote.",
                },
                {
                  icon: <InsightsIcon />,
                  title: "Análise e melhoria",
                  desc: "Indicadores, tratativas de não conformidades e ações corretivas.",
                },
                {
                  icon: <VerifiedIcon />,
                  title: "Conformidade e entrega",
                  desc: "Atendimento ao escopo técnico, prazo e comunicação transparente.",
                },
              ].map((p) => (
                <Grid key={p.title} item xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      p: 2,
                      height: "100%",
                      borderRadius: 3,
                      border: `1px solid ${BRAND.line}`,
                      background:
                        "linear-gradient(180deg, rgba(3,109,61,.04) 0%, rgba(3,109,61,.02) 100%)",
                      transition: "transform .2s ease, box-shadow .2s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 10px 28px rgba(0,0,0,.08)",
                      },
                    }}
                  >
                    <MKBox sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 0.75 }}>
                      <MKBox
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "rgba(3,109,61,.10)",
                          color: BRAND.main,
                          flex: "0 0 auto",
                        }}
                      >
                        {p.icon}
                      </MKBox>
                      <MKTypography variant="button" sx={{ fontWeight: 800, letterSpacing: ".4px" }}>
                        {p.title}
                      </MKTypography>
                    </MKBox>
                    <MKTypography variant="caption" color="text" sx={{ lineHeight: 1.7 }}>
                      {p.desc}
                    </MKTypography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Imagem de Qualidade (full width, responsiva) */}
            <Grid container spacing={2.5} sx={{ mt: { xs: 1, md: 2 }, mb: { xs: 3, md: 4 } }}>
              <Grid item xs={12}>
                <Card
                  sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    border: `1px solid ${BRAND.line}`,
                    boxShadow: "0 10px 24px rgba(0,0,0,.06)",
                  }}
                >
                  <MKBox
                    component="img"
                    src={qualidadeImg}
                    alt="Controle de qualidade — Ramlis Machine"
                    loading="lazy"
                    decoding="async"
                    sx={{
                      width: "100%",
                      display: "block",
                      maxHeight: { xs: 280, md: 420 },
                      objectFit: "cover",
                    }}
                  />
                  {/* legenda opcional */}
                  <MKTypography
                    variant="caption"
                    color="text"
                    sx={{ display: "block", px: 2, py: 1, opacity: 0.9 }}
                  >
                    Sala de metrologia e inspeções.
                  </MKTypography>
                </Card>
              </Grid>
            </Grid>

            {/* Política de Qualidade – visual aprimorado */}
            {/* Política de Qualidade – título no topo e itens 1 por linha */}
            <Card
              sx={{
                p: { xs: 2.75, md: 3.25 },
                borderRadius: 3,
                border: `1px solid ${BRAND.line}`,
                background: "linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,255,255,.99))",
                boxShadow: "0 12px 28px rgba(0,0,0,.06)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* filete lateral */}
              <MKBox
                aria-hidden
                sx={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 6,
                  background: "linear-gradient(180deg, rgba(3,109,61,1) 0%, rgba(3,109,61,.65) 100%)",
                }}
              />

              {/* cabeçalho */}
              <MKTypography variant="h6" fontWeight="bold" sx={{ color: BRAND.dark }}>
                Política de Qualidade
              </MKTypography>
              <MKTypography variant="caption" color="text" sx={{ opacity: 0.85 }}>
                Compromisso com satisfação do cliente, pessoas e melhoria contínua.
              </MKTypography>

              <Divider sx={{ my: 2, borderColor: BRAND.line, opacity: 0.6 }} />

              {/* lista 1 por linha */}
              <MKBox
                component="ul"
                sx={{
                  m: 0,
                  p: 0,
                  listStyle: "none",
                  display: "grid",
                  rowGap: 1.25,
                }}
              >
                {[
                  "Proporcionar a satisfação de nossos clientes.",
                  "Desenvolver e motivar continuamente nossos colaboradores, explorando seu pleno potencial.",
                  "Atuar com ética e transparência nas relações com clientes, fornecedores e colaboradores.",
                  "Buscar a melhoria contínua de nossos processos e serviços.",
                ].map((item) => (
                  <MKBox
                    key={item}
                    component="li"
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1.25, py: 0.25 }}
                  >
                    <MKBox
                      sx={{
                        width: 22,
                        height: 22,
                        mt: "2px",
                        borderRadius: "50%",
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "rgba(3,109,61,.14)",
                        color: BRAND.main,
                        flex: "0 0 auto",
                      }}
                    >
                      <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </MKBox>
                    <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.75 }}>
                      {item}
                    </MKTypography>
                  </MKBox>
                ))}
              </MKBox>
            </Card>

          </SectionContainer>
        </FadeIn>
      </Card>

      {/* Botão flutuante padrão */}
      <WhatsAppFloat />
    </>
  );
}
